(() => {
  if (window.GmlProductMobile) {
    window.GmlProductMobile.initialize();
    return;
  }

  const mobile = window.matchMedia('(max-width: 749px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();

  const createController = root => {
    const track = root.querySelector('[data-gml-mobile-track]');
    const slides = Array.from(root.querySelectorAll('[data-gml-mobile-slide]'));
    const pages = Array.from(root.querySelectorAll('[data-gml-mobile-page]'));
    const actions = root.querySelector('.gml-product__actions');
    const sticky = root.querySelector('[data-gml-mobile-sticky]');
    const header = document.querySelector('[data-gml-topbar]');
    if (!track || !actions || !sticky) return null;

    let enabled = false;
    let current = 0;
    let frame = 0;
    let resizeObserver;
    let modalObserver;
    let headerObserver;
    let accordionEntries = [];
    let replacedText = [];
    let stickyAnimation;

    const refreshGallery = () => {
      if (slides.length < 2) return;
      const left = track.getBoundingClientRect().left;
      let closest = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - left);
        if (distance < closest) { closest = distance; current = index; }
      });
      pages.forEach((page, index) => {
        if (index === current) page.setAttribute('aria-current', 'true');
        else page.removeAttribute('aria-current');
      });
    };

    const goToImage = index => {
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!enabled || slides.length < 2 || !target) return;
      // Only the gallery scrolls; scrollIntoView would also move the page vertically.
      track.scrollBy({
        left: target.getBoundingClientRect().left - track.getBoundingClientRect().left,
        behavior: reduced.matches ? 'auto' : 'smooth'
      });
    };
    const pageClick = event => {
      const page = event.target.closest('[data-gml-mobile-page]');
      if (page && root.contains(page)) goToImage(Number(page.dataset.gmlMobilePage));
    };
    const galleryKey = event => {
      if (event.target !== track || slides.length < 2) return;
      const rtl = getComputedStyle(track).direction === 'rtl';
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        goToImage(current + (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        goToImage(event.key === 'Home' ? 0 : slides.length - 1);
      }
    };

    const overlayOpen = () => document.body.classList.contains('blano-modal-open') ||
      Boolean(document.querySelector('dialog[open], [role="dialog"][aria-hidden="false"]'));

    const refreshSticky = () => {
      if (!enabled) return;
      const headerHidden = header?.hasAttribute('data-hidden') && !header.matches(':focus-within');
      const top = header && !headerHidden ? header.offsetHeight : 0;
      const value = `${top}px`;
      if (sticky.style.getPropertyValue('--gml-mobile-sticky-top') !== value) sticky.style.setProperty('--gml-mobile-sticky-top', value);
      const visible = actions.getBoundingClientRect().bottom <= top &&
        root.getBoundingClientRect().bottom > top + (sticky.offsetHeight || 68) && !overlayOpen();
      if (visible === !sticky.hidden) return;
      stickyAnimation?.cancel();
      sticky.hidden = !visible;
      if (visible && !reduced.matches && typeof sticky.animate === 'function') {
        stickyAnimation = sticky.animate(
          [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)' }
        );
      }
    };
    const schedule = () => {
      if (!enabled || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        refreshGallery();
        refreshSticky();
      });
    };

    const animateAccordion = entry => {
      const from = entry.details.getBoundingClientRect().height;
      entry.animation?.cancel();
      entry.animation = null;
      entry.expanded = !entry.expanded;
      entry.details.open = true;
      const target = entry.expanded
        ? entry.summary.getBoundingClientRect().height + entry.body.getBoundingClientRect().height + 1
        : entry.summary.getBoundingClientRect().height + 1;
      entry.summary.setAttribute('aria-expanded', String(entry.expanded));
      const finish = () => {
        entry.details.open = entry.expanded;
        entry.details.style.removeProperty('overflow');
        entry.animation = null;
        schedule();
      };
      if (reduced.matches || typeof entry.details.animate !== 'function') { finish(); return; }
      entry.details.style.overflow = 'hidden';
      const animation = entry.details.animate(
        [{ height: `${from}px` }, { height: `${target}px` }],
        { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
      entry.animation = animation;
      animation.finished.then(() => {
        if (enabled && entry.animation === animation) finish();
      }).catch(() => {});
    };

    const enable = () => {
      if (enabled) return;
      enabled = true;
      root.addEventListener('click', pageClick);
      track.addEventListener('scroll', schedule, { passive: true });
      track.addEventListener('keydown', galleryKey);
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('toggle', schedule, true);
      document.addEventListener('focusin', schedule);
      document.addEventListener('focusout', schedule);
      window.visualViewport?.addEventListener('resize', schedule, { passive: true });

      accordionEntries = Array.from(root.querySelectorAll('.gml-product__accordion:not(.gml-product__accordion--specifications)')).map(details => {
        const summary = details.querySelector(':scope > summary');
        const body = details.querySelector(':scope > .gml-product__description');
        if (!summary || !body) return null;
        const entry = {
          details, summary, body, expanded: details.open, originalOpen: details.open,
          originalStyle: details.getAttribute('style'), originalExpanded: summary.getAttribute('aria-expanded')
        };
        summary.setAttribute('aria-expanded', String(entry.expanded));
        entry.click = event => {
          if (event.defaultPrevented || event.button > 0 || event.target.closest('a,button,input,select,textarea')) return;
          event.preventDefault();
          animateAccordion(entry);
        };
        summary.addEventListener('click', entry.click);
        return entry;
      }).filter(Boolean);

      // The legacy reseller name is corrected only in mobile presentation; restore desktop exactly.
      const relatedHeading = root.querySelector('.gml-product__related-heading[data-gml-mobile-label]');
      if (relatedHeading?.firstChild && relatedHeading.dataset.gmlMobileLabel) {
        replacedText.push([relatedHeading.firstChild, relatedHeading.firstChild.nodeValue]);
        relatedHeading.firstChild.nodeValue = relatedHeading.dataset.gmlMobileLabel;
      }
      root.querySelectorAll('.gml-product__description').forEach(description => {
        const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT);
        let text;
        while ((text = walker.nextNode())) {
          if (!/\bBLANO\b/.test(text.nodeValue)) continue;
          replacedText.push([text, text.nodeValue]);
          text.nodeValue = text.nodeValue.replace(/\bBLANO\b/g, 'GMLTIME');
        }
      });

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(schedule);
        [track, actions, root, header].filter(Boolean).forEach(element => resizeObserver.observe(element));
      }
      modalObserver = new MutationObserver(schedule);
      modalObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
      document.querySelectorAll('dialog, [role="dialog"]').forEach(dialog => {
        modalObserver.observe(dialog, { attributes: true, attributeFilter: ['open', 'aria-hidden', 'class'] });
      });
      if (header) {
        headerObserver = new MutationObserver(schedule);
        headerObserver.observe(header, { attributes: true, attributeFilter: ['data-hidden'] });
      }
      schedule();
      document.fonts?.ready.then(schedule);
    };

    const disable = () => {
      if (!enabled) return;
      enabled = false;
      cancelAnimationFrame(frame);
      frame = 0;
      stickyAnimation?.cancel();
      sticky.hidden = true;
      sticky.style.removeProperty('--gml-mobile-sticky-top');
      root.removeEventListener('click', pageClick);
      track.removeEventListener('scroll', schedule);
      track.removeEventListener('keydown', galleryKey);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('toggle', schedule, true);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
      modalObserver?.disconnect();
      headerObserver?.disconnect();
      accordionEntries.forEach(entry => {
        entry.animation?.cancel();
        entry.summary.removeEventListener('click', entry.click);
        // The desktop controller may already have claimed the accordion on a wide resize.
        if (!root.classList.contains('gml-product--desktop-ready')) entry.details.open = entry.originalOpen;
        if (entry.originalStyle === null) entry.details.removeAttribute('style');
        else entry.details.setAttribute('style', entry.originalStyle);
        if (entry.originalExpanded === null) entry.summary.removeAttribute('aria-expanded');
        else entry.summary.setAttribute('aria-expanded', entry.originalExpanded);
      });
      replacedText.forEach(([text, original]) => { text.nodeValue = original; });
      replacedText = [];
      accordionEntries = [];
    };
    return { sync: () => mobile.matches ? enable() : disable(), destroy: disable };
  };

  const initialize = () => {
    document.querySelectorAll('[data-gml-mobile-watch]').forEach(root => {
      if (controllers.has(root)) return;
      const controller = createController(root);
      if (!controller) return;
      controllers.set(root, controller);
      controller.sync();
    });
  };
  mobile.addEventListener('change', () => controllers.forEach(controller => controller.sync()));
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => controllers.forEach((controller, root) => {
    if (event.target instanceof Element && event.target.contains(root)) {
      controller.destroy();
      controllers.delete(root);
    }
  }));
  window.GmlProductMobile = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
